/**
 * stayai-no-decimals.js
 * Strips decimal portions from prices rendered by the Stay AI subscription widget.
 * Also forces one-time purchase for wholesale customers.
 * Injects compare-at (strikethrough) price next to one-time purchase price
 * when the current variant has a compare_at_price.
 * Loaded as a deferred module so it never blocks page render.
 * Uses MutationObserver scoped to the widget container(s) to handle Stay AI's
 * async rendering and plan-selection re-renders. The active variant id itself
 * is tracked from the theme's own variant:update event rather than read live
 * from the DOM on every mutation, since the hidden variant-id input only
 * updates once the (potentially slow) variant fetch resolves — reading it
 * mid-fetch would use the previous variant's id and show a stale price.
 */

import { ThemeEvents } from '@theme/events';

const WIDGET_SELECTOR = '.stayai-widget, .stayai-subscriptions, purchase-options';
const PRICE_RE = /(\$[\d,]+)\.\d+/g;
const IS_WHOLESALE = document.body.getAttribute('data-customer-tags')?.split(' ')?.includes('wholesale');

function stripDecimals(root) {
	if (IS_WHOLESALE) return;
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	let node;
	while ((node = walker.nextNode())) {
		const updated = node.nodeValue.replace(PRICE_RE, '$1');
		if (updated !== node.nodeValue) node.nodeValue = updated;
	}
}

/**
 * Formats a price in cents to a human-readable string (e.g. 9500 → "$95").
 * Strips .00 decimals to match the widget's existing formatting.
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyNoDecimals(cents) {
	if (IS_WHOLESALE) {
		return '$' + (cents / 100).toFixed(2);
	}
	const dollars = cents / 100;
	if (dollars === Math.floor(dollars)) {
		return '$' + dollars;
	}
	return '$' + dollars.toFixed(2).replace(/\.00$/, '');
}

// Tracks the currently-active variant id per <purchase-options> widget, kept
// in sync via the theme's variant:update event (see observeWidget) rather
// than read live from the DOM, since the hidden variant-id input lags behind
// the actual selection until its (potentially slow) fetch resolves.
const widgetVariantIds = new WeakMap();

/**
 * Resolves the <purchase-options> element for a given widget container.
 * @param {Element} container
 * @returns {Element | null}
 */
function resolvePurchaseOptions(container) {
	return container.matches('purchase-options') ? container : container.closest('purchase-options');
}

/**
 * Finds the product-form-component belonging to the same product as this
 * widget by walking up from the widget to the nearest ancestor that contains
 * one, rather than searching the whole document (which can match an
 * unrelated product-form-component elsewhere on the page, e.g. an
 * upsell/featured-product block or the quick-add modal).
 * @param {Element} purchaseOptions - The <purchase-options> element.
 * @returns {Element | null}
 */
function findScopedProductFormComponent(purchaseOptions) {
	let scope = purchaseOptions.parentElement;
	while (scope) {
		const component = scope.querySelector('product-form-component');
		if (component) return component;
		scope = scope.parentElement;
	}
	return null;
}

/**
 * Injects a compare-at (strikethrough) price beside the one-time purchase price
 * when the current variant has a compare_at_price greater than its price.
 * @param {Element} container - The widget container element.
 */
function injectCompareAtPrice(container) {
	const purchaseOptions = resolvePurchaseOptions(container);
	if (!purchaseOptions) return;

	const productDataStr = purchaseOptions.getAttribute('productdata');
	if (!productDataStr) return;

	/** @type {{ variants?: Array<{ id: number, price: number, compare_at_price: number|null }> } | null} */
	let productData;
	try {
		productData = JSON.parse(productDataStr);
	} catch (e) {
		return;
	}

	// Prefer the variant id tracked from the authoritative variant:update event
	// (see observeWidget); fall back to the Stay.ai currentvariantid attribute
	// only before that event has fired for this widget (e.g. right at page load).
	const targetVariantId = widgetVariantIds.get(purchaseOptions) ?? purchaseOptions.getAttribute('currentvariantid');
	if (!targetVariantId) return;

	const variant = productData?.variants?.find(
		(v) => String(v.id) === String(targetVariantId)
	);
	if (!variant) return;

	const compareAtPrice = variant.compare_at_price;
	const price = variant.price;

	// Walk up from container to find the one-time-price span (it may be in a child)
	const oneTimePriceEl = container.querySelector('[data-stayai-onetime-price]');
	if (!oneTimePriceEl) return;

	const existingWrapper = oneTimePriceEl.parentElement?.closest('.stayai-compare-at-wrapper');
	const existingCompareAt = existingWrapper?.querySelector('.stayai-compare-at');

	const shouldShowCompareAt = compareAtPrice && compareAtPrice > price;

	if (shouldShowCompareAt) {
		const formattedPrice = formatMoneyNoDecimals(compareAtPrice);

		// Already wrapped — just update the text if it changed (avoids infinite DOM mutation loop)
		if (existingWrapper && existingCompareAt) {
			if (existingCompareAt.textContent !== formattedPrice) {
				existingCompareAt.textContent = formattedPrice;
			}
			return;
		}

		const s = document.createElement('s');
		s.className = 'fullPrice stayai-compare-at';
		s.textContent = formattedPrice;
		s.style.setProperty('color', 'var(--stayai-selected-text-color)', 'important');
		s.style.setProperty('text-decoration-color', 'var(--stayai-selected-text-color)', 'important');
		s.style.setProperty('font-weight', 'bold', 'important');

		// Wrap both in a span so space-between doesn't push them apart
		const wrapper = document.createElement('span');
		wrapper.className = 'stayai-compare-at-wrapper';
		wrapper.style.display = 'inline-flex';
		wrapper.style.alignItems = 'center';
		oneTimePriceEl.insertAdjacentElement('beforebegin', wrapper);
		wrapper.appendChild(s);
		wrapper.appendChild(oneTimePriceEl);
	} else if (existingWrapper) {
		// Variant no longer has a compare-at — unwrap and clean up
		existingWrapper.parentElement?.insertBefore(oneTimePriceEl, existingWrapper);
		existingWrapper.remove();
	}
}

/**
 * For wholesale customers: attach a delegated click listener to the widget
 * that intercepts clicks on the subscription option and clicks the
 * one-time purchase radio instead. Also clicks one-time on initial render.
 */
function setupWholesale(container) {
	if (!IS_WHOLESALE) return;
	if (container.dataset.wholesaleHandled) return;
	container.dataset.wholesaleHandled = 'true';

	// Click one-time purchase after a short delay so the widget finishes initializing
	setTimeout(() => {
		const oneTime = container.querySelector('#purchaseTypeOneTime');
		if (oneTime) oneTime.click();
	}, 100);

	// Intercept any click on the subscription option
	container.addEventListener('click', (e) => {
		const target = /** @type {HTMLElement} */ (e.target);
		if (target.closest('[for="purchaseTypeSubscription"]') || target.closest('#purchaseTypeSubscription')) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			const oneTime = container.querySelector('#purchaseTypeOneTime');
			if (oneTime) oneTime.click();
		}
	}, true);
}

function observeWidget(container) {
	const purchaseOptions = resolvePurchaseOptions(container);
	if (purchaseOptions && !widgetVariantIds.has(purchaseOptions)) {
		const productFormComponent = findScopedProductFormComponent(purchaseOptions);

		// Seed the cache from the currently-rendered variant id. At this point
		// (page load, or before any variant switch) the hidden input reflects
		// the real selection, so this initial read is safe.
		const initialVariantId =
			productFormComponent?.querySelector('input[name="id"]')?.value ||
			purchaseOptions.getAttribute('currentvariantid');
		if (initialVariantId) widgetVariantIds.set(purchaseOptions, initialVariantId);

		const productId = productFormComponent?.dataset.productId;
		if (productId) {
			document.addEventListener(ThemeEvents.variantUpdate, (event) => {
				if (event.detail?.data?.productId !== productId) return;
				const newVariantId = event.detail.resource?.id;
				if (!newVariantId) return;
				widgetVariantIds.set(purchaseOptions, newVariantId);
				stripDecimals(container);
				injectCompareAtPrice(container);
			});
		}
	}

	stripDecimals(container);
	setupWholesale(container);
	injectCompareAtPrice(container);

	let pending = false;
	new MutationObserver(() => {
		if (pending) return;
		pending = true;
		requestAnimationFrame(() => {
			stripDecimals(container);
			injectCompareAtPrice(container);
			pending = false;
		});
	}).observe(container, {
		childList: true,
		subtree: true,
		characterData: true,
		attributes: true,
		attributeFilter: ['currentvariantid', 'productdata'],
	});
}

function init() {
	const containers = document.querySelectorAll(WIDGET_SELECTOR);

	if (containers.length) {
		containers.forEach(observeWidget);
		return;
	}

	// Widget not yet in DOM — watch body until it appears, then hand off
	const bodyObs = new MutationObserver(() => {
		const found = document.querySelectorAll(WIDGET_SELECTOR);
		if (!found.length) return;
		bodyObs.disconnect();
		found.forEach(observeWidget);
	});
	bodyObs.observe(document.body, { childList: true, subtree: true });
}

init();
